import Config from '../_config/config';
import type {
  Timetable,
  TimetableDetail,
  ApiResponse,
  TeacherScheduleData,
  TeacherScheduleFilters,
  BirdsEyeViewData,
  BirdsEyeViewFilters,
} from '../_models/schedule.model';

const API_BASE_URL = Config.nodeSiteUrl;

// Helper function to get headers with authentication
const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const currentUser = localStorage.getItem('currentUser');
  if (currentUser && currentUser !== 'undefined') {
    try {
      const user = JSON.parse(currentUser);
      const token = user.token;
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Failed to parse currentUser from localStorage:', error);
    }
  }

  return headers;
};

/**
 * Get all timetables
 */
export const getTimetables = async (): Promise<ApiResponse<Timetable[]>> => {
  const url = `${API_BASE_URL}v1/schedules`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Get timetable detail with activity slots
 */
export const getTimetableDetail = async (id: number): Promise<ApiResponse<TimetableDetail>> => {
  const url = `${API_BASE_URL}v1/schedules/${id}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Get teacher's schedule with analytics and filtering
 */
export const getTeacherSchedule = async (
  teacherId: number,
  filters?: TeacherScheduleFilters
): Promise<ApiResponse<TeacherScheduleData>> => {
  const queryParams = new URLSearchParams();

  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);
  if (filters?.batchIds) queryParams.append('batchIds', filters.batchIds);
  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.timetableIds) queryParams.append('timetableIds', filters.timetableIds);

  const url = `${API_BASE_URL}v1/schedules/teacher/${teacherId}${
    queryParams.toString() ? '?' + queryParams.toString() : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Search for instructors by name, code, email, or phone
 */
export interface Instructor {
  id: number;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  code: string | null;
  email: string | null;
  phoneNumber: string | null;
  isTrainer: number | null;
}

export const searchInstructors = async (query: string): Promise<ApiResponse<Instructor[]>> => {
  const url = `${API_BASE_URL}v1/schedules/instructors/search?query=${encodeURIComponent(query)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Get bird's eye view of all activity slots with filtering
 */
export const getBirdsEyeView = async (
  filters?: BirdsEyeViewFilters
): Promise<ApiResponse<BirdsEyeViewData>> => {
  const queryParams = new URLSearchParams();

  if (filters?.startDate) queryParams.append('startDate', filters.startDate);
  if (filters?.endDate) queryParams.append('endDate', filters.endDate);
  if (filters?.instructorIds) queryParams.append('instructorIds', filters.instructorIds);
  if (filters?.batchIds) queryParams.append('batchIds', filters.batchIds);
  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.timetableIds) queryParams.append('timetableIds', filters.timetableIds);
  if (filters?.roomIds) queryParams.append('roomIds', filters.roomIds);
  if (filters?.shiftIds) queryParams.append('shiftIds', filters.shiftIds);
  if (filters?.centreIds) queryParams.append('centreIds', filters.centreIds);

  const url = `${API_BASE_URL}v1/schedules/birds-eye-view${
    queryParams.toString() ? '?' + queryParams.toString() : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Update activity slot status
 */
export const updateActivitySlotStatus = async (
  slotId: number,
  status: 'Scheduled' | 'Conducted' | 'Cancelled',
  remarks?: string
): Promise<ApiResponse<any>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/${slotId}/status`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status, remarks }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Bulk update activity slot statuses
 */
export const bulkUpdateActivitySlotStatus = async (
  slotIds: number[],
  status: 'Scheduled' | 'Conducted' | 'Cancelled',
  remarks?: string
): Promise<ApiResponse<{ successCount: number; failureCount: number }>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/bulk/status`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ slotIds, status, remarks }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete all timetables and optionally unassigned activity slots
 */
export const deleteAllTimetables = async (
  includeUnassignedSlots: boolean
): Promise<ApiResponse<{
  deletedCounts: {
    timetables: number;
    mappedActivitySlots: number;
    unmappedActivitySlots: number;
    totalMappings: number;
  };
  unassignedSlotsDeleted: boolean;
}>> => {
  const url = `${API_BASE_URL}v1/schedules/delete-all`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ includeUnassignedSlots }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Update an activity slot
 */
export const updateActivitySlot = async (
  slotId: number,
  slotData: any
): Promise<ApiResponse<any>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/${slotId}`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(slotData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete an activity slot
 */
export const deleteActivitySlot = async (
  slotId: number
): Promise<ApiResponse<any>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/${slotId}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Add a new activity slot to a timetable
 */
export const addActivitySlot = async (
  timetableId: number,
  slotData: any
): Promise<ApiResponse<any>> => {
  const url = `${API_BASE_URL}v1/schedules/${timetableId}/activity-slots`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(slotData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Validate if a slot can be moved to a new date/time
 */
export const validateSlotMove = async (
  slotId: number,
  moveData: {
    newDate: string;
    newStartTime: string;
    newEndTime: string;
    instructorId?: number;
    roomId?: number;
  }
): Promise<ApiResponse<{
  valid: boolean;
  conflicts: Array<{
    type: 'room' | 'instructor';
    message: string;
    conflictingSlot: any;
  }>;
}>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/${slotId}/validate-move`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(moveData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Propagate slot changes to downstream weeks
 */
export const propagateSlotChanges = async (
  slotId: number,
  propagateData: {
    newDate: string;
    newStartTime: string;
    newEndTime: string;
    instructorId?: number;
    roomId?: number;
    weeks?: number;
  }
): Promise<ApiResponse<{
  updatedCount: number;
  updatedSlots: Array<any>;
}>> => {
  const url = `${API_BASE_URL}v1/schedules/activity-slots/${slotId}/propagate`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(propagateData),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};
